import {
    CoaxPortMode,
    ComprimatoPortMode,
    IpPortMode,
    MatroxPortMode,
    NmosPortMode,
    SrtMode,
    VideonPortMode,
    ZixiMode,
    type OutputPort,
} from './api/v1/types'

/** Shown when an input or output has no port. A port with an unknown mode is {@link UNKNOWN_TYPE_LABEL}. */
export const NOT_CONFIGURED_TYPE_LABEL = 'Not configured'
export const UNKNOWN_TYPE_LABEL = 'N/A'

/**
 * Chip text for one raw port mode. SDI is many modes to one label, which is why sorting on the
 * raw mode splits that group. Zixi and SRT are absent: their chip includes the sub-mode.
 */
export const PORT_MODE_TYPE_LABELS: Readonly<Record<string, string>> = {
    [CoaxPortMode.sdi]: 'SDI',
    [MatroxPortMode.matroxSdi]: 'SDI',
    [ComprimatoPortMode.comprimatoSdi]: 'SDI',
    [CoaxPortMode.asi]: 'ASI',
    [ComprimatoPortMode.comprimatoNdi]: 'NDI',
    [VideonPortMode.videonAuto]: 'Videon auto',
    [VideonPortMode.videonSdi]: 'Videon SDI',
    [VideonPortMode.videonHdmi]: 'Videon HDMI',
    [IpPortMode.rist]: 'RIST',
    [IpPortMode.rtmp]: 'RTMP',
    [IpPortMode.rtp]: 'RTP',
    [NmosPortMode.nmos]: 'NMOS',
    [IpPortMode.udp]: 'UDP',
    [IpPortMode.generator]: 'Generator',
}

/** Modes the input chip names and the output chip leaves as N/A. */
export const INPUT_ONLY_PORT_MODES: ReadonlySet<string> = new Set([
    IpPortMode.generator,
    VideonPortMode.videonAuto,
    VideonPortMode.videonSdi,
    VideonPortMode.videonHdmi,
])

export const ZIXI_MODE_TYPE_LABELS: Readonly<Record<string, string>> = {
    [ZixiMode.pull]: 'Zixi pull',
    [ZixiMode.push]: 'Zixi push',
}
export const ZIXI_TYPE_LABEL = 'Zixi'

export const SRT_MODE_TYPE_LABELS: Readonly<Record<string, string>> = {
    [SrtMode.listener]: 'SRT listener',
    [SrtMode.caller]: 'SRT caller',
    [SrtMode.rendezvous]: 'SRT rendezvous',
}
export const SRT_TYPE_LABEL = 'SRT'

export const TRANSFORMATION_TYPE_LABELS: Readonly<Record<string, string>> = {
    transcode: 'Transcode',
    'mpts-demux': 'MPTS Demux',
    'audio-reshuffling': 'Audio Reshuffling',
    'ad-insertion': 'Ad Insertion',
    'pid-remapping': 'PID Remapping',
    'framerate-conversion': 'Frame-rate Conversion',
}

export interface PortModeSubmodes {
    zixiMode?: string | null
    srtMode?: string | null
}

export function formatTransformationType(type: string): string {
    const known = TRANSFORMATION_TYPE_LABELS[type]
    if (known) return known
    const spaced = type.replace(/-/g, ' ')
    return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function labelForMode(mode: string, submodes: PortModeSubmodes | undefined, includeInputOnly: boolean): string {
    if (mode === IpPortMode.zixi) {
        const zixiMode = submodes?.zixiMode
        return (zixiMode && ZIXI_MODE_TYPE_LABELS[zixiMode]) || ZIXI_TYPE_LABEL
    }
    if (mode === IpPortMode.srt) {
        const srtMode = submodes?.srtMode
        return (srtMode && SRT_MODE_TYPE_LABELS[srtMode]) || SRT_TYPE_LABEL
    }
    if (!includeInputOnly && INPUT_ONLY_PORT_MODES.has(mode)) return UNKNOWN_TYPE_LABEL
    return PORT_MODE_TYPE_LABELS[mode] ?? UNKNOWN_TYPE_LABEL
}

/** Label for a port that exists. A missing mode is N/A; the caller decides when there is no port. */
export function inputPortModeLabel(mode: string | null | undefined, submodes?: PortModeSubmodes): string {
    if (!mode) return UNKNOWN_TYPE_LABEL
    return labelForMode(mode, submodes, true)
}

export function outputPortModeLabel(mode: string | null | undefined, submodes?: PortModeSubmodes): string {
    if (!mode) return UNKNOWN_TYPE_LABEL
    return labelForMode(mode, submodes, false)
}

export interface ListedInputType {
    deriveFrom?: { ingestTransform?: { type: string } | null } | null
    ports?: Array<{ mode?: string | null } & PortModeSubmodes> | null
}

/**
 * Sort key for one input row. Derived inputs show the transformation, and their ports are not
 * part of the response. Several chips on one row sort by the first port: the column has no
 * single label to join, and the list query already orders by that port.
 */
export function inputListTypeLabel(input: ListedInputType): string {
    if (input.deriveFrom) {
        const type = input.deriveFrom.ingestTransform?.type
        return type ? formatTransformationType(type) : NOT_CONFIGURED_TYPE_LABEL
    }
    const port = input.ports?.[0]
    if (!port) return NOT_CONFIGURED_TYPE_LABEL
    return inputPortModeLabel(port.mode, port)
}

export function outputListTypeLabel(port?: OutputPort): string {
    if (!port) return NOT_CONFIGURED_TYPE_LABEL
    if (port.mode === IpPortMode.zixi) return outputPortModeLabel(port.mode, { zixiMode: port.zixiMode })
    if (port.mode === IpPortMode.srt) return outputPortModeLabel(port.mode, { srtMode: port.srtMode })
    return outputPortModeLabel(port.mode)
}
